import type { Engine } from "../../../engine/Engine";
import type {
  PointerGestureMoveDetail,
  PointerGesturePressDetail,
  PointerGestureReleaseDetail,
} from "../../../input/PointerGestureInterpreter";
import {
  SEGMENT_POINTER_CHANGED_EVENT,
} from "../../../shape/segment/segmentPointerEvents";
import type { XzPoint } from "../../../shape/segment/types";
import type IRoomDrawer from "../types/IRoomDrawer";
import type { DesignerTransformer } from "../../../core/drag/DesignerTransformer";
import type { DesignerPointerInteraction } from "./DesignerPointerInteraction";
import { ProductTargetPicker } from "./pick/ProductTargetPicker";
import { Opening3DTargetPicker } from "./pick/Opening3DTargetPicker";
import { UnifiedSegmentTargetPicker } from "./pick/SegmentTargetPicker";
import { resolveVisualTarget } from "./pick/VisualDepthResolver";
import type { TargetCandidate } from "./TargetCandidate";
import type { SugarModel } from "../../../loader/SugarModel";
import type {
  DesignerMoveSession,
  DesignerObject,
  DesignerPointerFeedback,
} from "../object/DesignerObject";
import type { DesignerSelection } from "../object/DesignerSelection";
import { ProductDesignerObject } from "../object/ProductDesignerObject";
import { SegmentDesignerObject } from "../object/SegmentDesignerObject";
import type { SegmentInteractionTarget } from "../segment/interaction/DesignerTarget";
import type { SnapKind } from "../../../snap/SnapEngine";
import type { DesignerElementEventChannels } from "../../../core/events/ElementEventPlatform";
import { DesignerChannels } from "../../../core/events/DesignerChannels";
import {
  DESIGNER_DRAG_ENDED_EVENT,
  DESIGNER_DRAG_STARTED_EVENT,
} from "../../../core/events/DesignerEventContracts";
import type {
  ProductGizmoCapture,
  ProductGizmoMode,
} from "../transform/ProductTranslateGizmo";

/** Owns select-mode picking, hover, selection, drag sessions and capture. */
export class SelectInteractionController implements DesignerPointerInteraction {
  private readonly _productPicker: ProductTargetPicker;
  private readonly _segmentPicker: UnifiedSegmentTargetPicker;
  private readonly _opening3dPicker: Opening3DTargetPicker;
  private _session: DesignerMoveSession | null = null;
  private _target: DesignerObject | null = null;
  private _hovered: DesignerObject | null = null;
  private _pressScreen: XzPoint | null = null;
  private _capturedPointer: number | null = null;
  private _cameraWasEnabled: boolean | null = null;
  private _active = false;
  /** Empty-canvas press: deselect only if it stays a click, not an orbit/pan. */
  private _pendingEmptyDeselect = false;
  private static readonly _EMPTY_CLICK_PX = 4;

  /** Always present — falls back to a private platform (see DesignerChannels). */
  private readonly _channels: DesignerChannels;

  constructor(
    private readonly _engine: Engine,
    private readonly _drawer: IRoomDrawer,
    transformer: DesignerTransformer,
    private readonly _selection: DesignerSelection,
    deleteProduct: (model: SugarModel) => boolean,
    deleteSegment: (segmentId: string) => boolean,
    _events?: DesignerElementEventChannels,
    private readonly _gizmoCapture?: ProductGizmoCapture,
    private readonly _gizmoMode?: ProductGizmoMode,
  ) {
    this._channels = new DesignerChannels(_events);
    this._channels.bindEngine(_engine);
    this._productPicker = new ProductTargetPicker(
      transformer.scene,
      _engine,
      () => transformer.selectedModel,
      (model, sourceModel, hit, intersections) =>
        new ProductDesignerObject(
          model,
          sourceModel,
          hit,
          intersections,
          transformer,
          deleteProduct,
        ),
    );
    const createSegmentObject = (
      target: SegmentInteractionTarget,
      raw: XzPoint,
      snapped: XzPoint,
      snappedKinds: SnapKind[],
    ) =>
      new SegmentDesignerObject(
        target,
        _drawer,
        _engine,
        raw,
        snapped,
        snappedKinds,
        deleteSegment,
        this._dispatchSegmentFeedback,
      );
    this._segmentPicker = new UnifiedSegmentTargetPicker(
      _drawer.planarGraphController,
      _drawer.selectInteraction.picker,
      _engine,
      createSegmentObject,
    );
    this._opening3dPicker = new Opening3DTargetPicker(
      _drawer.planarGraphController,
      _engine,
      createSegmentObject,
    );
    this._gizmoCapture?.subscribe((active) => {
      if (!active) return;
      this._setHover(null);
      this._clearSegmentPointer();
      this._pendingEmptyDeselect = false;
    });
  }

  activate(): void {
    if (this._active) return;
    this._active = true;
    this._engine.canvas.addEventListener("pointerleave", this._onPointerLeave);
  }

  deactivate(): void {
    if (!this._active) return;
    this.cancel();
    this._setHover(null);
    this._clearSegmentPointer();
    this._engine.canvas.removeEventListener("pointerleave", this._onPointerLeave);
    this._active = false;
  }

  press(detail: PointerGesturePressDetail): void {
    if (
      this._gizmoCapture?.active ||
      this._gizmoCapture?.hitsPointer(detail.clientX, detail.clientY)
    ) {
      this._pendingEmptyDeselect = false;
      return;
    }
    if (!this._isActivator(detail) || this._session) return;
    if (detail.activePointerCount !== 1) return;
    const candidate = this._pick(detail);
    this._pressScreen = { x: detail.clientX, z: detail.clientY };
    if (!candidate) {
      // OrbitControls left-drag starts on empty canvas. Clearing here made
      // camera rotate drop the product selection (and hide bbox meters).
      this._pendingEmptyDeselect = true;
      return;
    }
    this._pendingEmptyDeselect = false;
    this._select(candidate, detail);
    if (
      this._gizmoMode?.blocksProductDrag &&
      candidate.object instanceof ProductDesignerObject
    ) {
      return;
    }
    if (!candidate.selectOnly) this._begin(candidate, detail);
  }

  move(detail: PointerGestureMoveDetail): void {
    if (!detail) return;
    if (this._pendingEmptyDeselect) this._cancelEmptyDeselectIfDragged(detail);
    if (this._session) {
      if (detail.activePointerCount !== 1) {
        this.cancel();
        return;
      }
      try {
        this._session.update(detail);
      } catch (err) {
        console.error("Product/segment drag update failed", err);
        this.cancel();
      }
      return;
    }
    if (detail.activePointerCount > 1) return;
    const candidate = this._pick(detail as PointerGesturePressDetail);
    this._setHover(candidate?.object ?? null);
    const feedback = candidate?.object.pointerFeedback?.();
    if (feedback) {
      this._dispatchSegmentFeedback(feedback);
    } else {
      this._clearSegmentPointer();
    }
  }

  release(detail: PointerGestureReleaseDetail): void {
    if (this._flushEmptyDeselect(detail)) return;
    if (!detail || !this._session) return;
    const click = Boolean(
      this._pressScreen &&
        Math.hypot(
          detail.clientX - this._pressScreen.x,
          detail.clientY - this._pressScreen.z,
        ) < 1,
    );
    try {
      if (click && !this._session.commitClick) {
        this._session.cancel();
        this._target?.activate?.();
      } else {
        this._session.commit(detail);
      }
      this._session.dispose();
      this._emitDragEnded();
    } catch (err) {
      console.error("Product/segment drag release failed", err);
      try {
        this._session?.cancel();
      } catch {
        /* ignore */
      }
      try {
        this._session?.dispose();
      } catch {
        /* ignore */
      }
      this._emitDragEnded();
    } finally {
      this._finishSession();
    }
    if (detail.isDoubleClick) {
      const candidate = this._pick(detail, true);
      if (candidate) this._select(candidate, detail);
    }
  }

  cancel(): void {
    this._pendingEmptyDeselect = false;
    try {
      if (this._session) {
        try {
          this._session.cancel();
        } catch (err) {
          console.error("Product/segment drag cancel failed", err);
        }
        try {
          this._session.dispose();
        } catch {
          /* ignore */
        }
        this._emitDragEnded();
      }
    } finally {
      this._finishSession();
    }
  }

  private _pick(
    detail: PointerGesturePressDetail,
    doubleClick = false,
  ): TargetCandidate | null {
    // 2D: full segment tool pick (walls + openings on floor plane).
    // 3D: segmentToolsEnabled is false — only raycast door/window fixtures.
    const segmentCandidate = this._drawer.segmentToolsEnabled
      ? this._segmentPicker.pick(detail)
      : this._opening3dPicker.pick(detail);
    return resolveVisualTarget([
      this._productPicker.pick(detail, doubleClick),
      segmentCandidate,
    ]);
  }

  private _begin(candidate: TargetCandidate, detail: PointerGesturePressDetail): void {
    this._lockCamera();
    const session = candidate.object.beginMove(detail);
    if (!session) {
      candidate.object.activate?.();
      this._unlockCamera();
      return;
    }
    this._session = session;
    this._emitDragStarted();
    this._capturedPointer = detail.pointerId;
    this._engine.canvas.setPointerCapture?.(detail.pointerId);
    detail.sourceEvent?.stopImmediatePropagation();
  }

  private _select(candidate: TargetCandidate, detail: PointerGesturePressDetail): void {
    this._target = candidate.object;
    this._setHover(null);
    this._selection.select(candidate.object, detail);
  }

  private _clearSelection(): void {
    this._target = null;
    this._pendingEmptyDeselect = false;
    this._selection.clear();
  }

  private _emptyPressMoved(
    clientX: number,
    clientY: number,
  ): boolean {
    if (!this._pressScreen) return false;
    return (
      Math.hypot(
        clientX - this._pressScreen.x,
        clientY - this._pressScreen.z,
      ) >= SelectInteractionController._EMPTY_CLICK_PX
    );
  }

  private _cancelEmptyDeselectIfDragged(detail: {
    clientX: number;
    clientY: number;
  }): void {
    if (!this._pendingEmptyDeselect) return;
    if (this._emptyPressMoved(detail.clientX, detail.clientY)) {
      this._pendingEmptyDeselect = false;
    }
  }

  private _flushEmptyDeselect(
    detail?: PointerGestureReleaseDetail,
  ): boolean {
    if (!this._pendingEmptyDeselect) return false;
    this._pendingEmptyDeselect = false;
    const moved = Boolean(
      detail && this._emptyPressMoved(detail.clientX, detail.clientY),
    );
    if (!moved) this._clearSelection();
    this._pressScreen = null;
    return true;
  }

  private _setHover(target: DesignerObject | null): void {
    if (this._hovered?.id === target?.id) return;
    this._hovered?.setHovered(false);
    this._hovered = target;
    this._hovered?.setHovered(true);
  }

  private _onPointerLeave = (): void => {
    if (this._session) return;
    this._setHover(null);
    this._clearSegmentPointer();
  };

  private _finishSession(): void {
    if (this._capturedPointer !== null) {
      try {
        this._engine.canvas.releasePointerCapture?.(this._capturedPointer);
      } catch {}
    }
    this._capturedPointer = null;
    this._session = null;
    this._pressScreen = null;
    this._unlockCamera();
  }

  private _lockCamera(): void {
    const controller = this._engine.activeCamera?.controller;
    if (!controller || this._cameraWasEnabled !== null) return;
    // UI catalog grab already set enabled=false via dragging-start. Do not
    // snapshot that false — unlock after drop would write it back and kill orbit.
    if (!controller.enabled) return;
    this._cameraWasEnabled = true;
    controller.enabled = false;
  }

  private _unlockCamera(): void {
    const controller = this._engine.activeCamera?.controller;
    if (controller && this._cameraWasEnabled === true) {
      controller.enabled = true;
    }
    this._cameraWasEnabled = null;
  }

  private _dispatchSegmentFeedback = (
    feedback: DesignerPointerFeedback,
  ): void => {
    this._engine.dispatchCustomEvent(SEGMENT_POINTER_CHANGED_EVENT, {
      detail: {
        ...feedback,
        active: true,
        planarGraph: this._drawer.planarGraphController.planarGraph,
      },
    });
  };

  private _clearSegmentPointer(): void {
    this._engine.dispatchCustomEvent(SEGMENT_POINTER_CHANGED_EVENT, {
      detail: {
        raw: { x: 0, z: 0 },
        snapped: { x: 0, z: 0 },
        snappedKinds: [],
        pickTarget: null,
        active: false,
        planarGraph: this._drawer.planarGraphController.planarGraph,
      },
    });
  }

  private _isActivator(detail?: PointerGesturePressDetail): boolean {
    if (!detail || !detail.isPrimary) return false;
    return detail.pointerType !== "mouse" ||
      (detail.button === 0 &&
        !detail.modifiers.ctrl &&
        !detail.modifiers.meta &&
        !detail.modifiers.shift);
  }

  private _emitDragStarted(): void {
    this._channels.channels.transform.events.emit(
      DESIGNER_DRAG_STARTED_EVENT,
      undefined,
    );
  }

  private _emitDragEnded(): void {
    this._channels.channels.transform.events.emit(
      DESIGNER_DRAG_ENDED_EVENT,
      undefined,
    );
  }
}
